import { useState, useEffect } from "react";
import {
  useRouteError,
  isRouteErrorResponse,
  Links,
  Meta,
  Scripts,
} from "@remix-run/react";

function generateErrorId(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "ERR-";
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function ErrorBoundary() {
  const error = useRouteError();
  const [copied, setCopied] = useState(false);
  // Initialize as null to avoid hydration mismatch - set after mount
  const [errorId, setErrorId] = useState<string | null>(null);
  const [timestamp, setTimestamp] = useState<string | null>(null);

  // Generate error ID and timestamp only on the client after hydration
  useEffect(() => {
    setErrorId(generateErrorId());
    setTimestamp(new Date().toISOString());
  }, []);

  let errorTitle = "Oops! Something went wrong";
  let errorMessage =
    "We encountered an unexpected error. Don't worry, this has been logged and we'll look into it.";
  let statusCode: number | undefined;

  if (isRouteErrorResponse(error)) {
    statusCode = error.status;

    switch (error.status) {
      case 404:
        errorTitle = "Page Not Found";
        errorMessage =
          "Sorry, we couldn't find the page you're looking for. It might have been moved or deleted.";
        break;
      case 401:
        errorTitle = "Unauthorized";
        errorMessage =
          "You don't have permission to access this resource. Please log in and try again.";
        break;
      case 403:
        errorTitle = "Forbidden";
        errorMessage =
          "Access to this resource is forbidden. Please contact your administrator if you believe this is an error.";
        break;
      case 500:
        errorTitle = "Server Error";
        errorMessage =
          "Our server encountered an error while processing your request. We're working on fixing it.";
        break;
      default:
        errorTitle = `Error ${error.status}`;
        errorMessage = error.statusText || error.data?.message || errorMessage;
    }
  } else if (error instanceof Error) {
    errorMessage = error.message;
  }

  // Get error stack for debug info
  const errorStack = error instanceof Error ? error.stack : undefined;

  // Generate debug info for clipboard
  const getDebugInfo = (): string => {
    const url = typeof window !== "undefined" ? window.location.href : "N/A";
    const browser = typeof navigator !== "undefined" ? navigator.userAgent : "N/A";

    return `=== Trash Fairies Error Report ===
Error ID: ${errorId ?? "N/A"}
Time: ${timestamp ?? "N/A"}
URL: ${url}
Status: ${statusCode ?? "N/A"}
Error: ${errorMessage}
Stack:
${errorStack ?? "No stack trace available"}
Browser: ${browser}
=====================================`;
  };

  // Only show debug section after client-side hydration
  const isHydrated = errorId !== null;

  const handleCopyDebugInfo = async () => {
    try {
      await navigator.clipboard.writeText(getDebugInfo());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = getDebugInfo();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin=""
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap"
          rel="stylesheet"
        />
        <title>{errorTitle}</title>
        <Meta />
        <Links />
        <style>{`
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          html, body {
            height: 100%;
            font-family: Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
            background: #000000;
            color: #ffffff;
          }

          .error-container {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 2rem;
            background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%);
          }

          .error-content {
            max-width: 600px;
            width: 100%;
            text-align: center;
          }

          .error-icon {
            width: 120px;
            height: 120px;
            margin: 0 auto 2rem;
            border-radius: 50%;
            background: linear-gradient(135deg, #10EFC7 0%, #00614F 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 8px 32px rgba(16, 239, 199, 0.3);
          }

          .error-icon svg {
            width: 64px;
            height: 64px;
            stroke: #000000;
          }

          .error-card {
            background: rgba(26, 26, 26, 0.95);
            border: 1px solid rgba(16, 239, 199, 0.2);
            border-radius: 12px;
            padding: 2.5rem;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
          }

          .status-code {
            font-size: 3rem;
            font-weight: 700;
            color: #10EFC7;
            letter-spacing: 4px;
            margin-bottom: 1rem;
          }

          .error-title {
            font-size: 2rem;
            font-weight: 700;
            color: #ffffff;
            margin-bottom: 1rem;
          }

          .error-message {
            font-size: 1.125rem;
            color: rgba(255, 255, 255, 0.8);
            line-height: 1.6;
            margin-bottom: 2rem;
          }

          .error-details {
            background: rgba(0, 0, 0, 0.5);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            padding: 1rem;
            margin-top: 1.5rem;
            text-align: left;
          }

          .error-details-title {
            font-size: 0.875rem;
            font-weight: 600;
            color: #10EFC7;
            margin-bottom: 0.5rem;
          }

          .error-stack {
            font-size: 0.75rem;
            color: rgba(255, 255, 255, 0.7);
            font-family: 'Courier New', monospace;
            white-space: pre-wrap;
            word-break: break-word;
            max-height: 200px;
            overflow-y: auto;
          }

          .button-group {
            display: flex;
            gap: 1rem;
            justify-content: center;
            margin-top: 2rem;
          }

          .btn {
            display: inline-block;
            padding: 0.875rem 2rem;
            font-size: 1.125rem;
            font-weight: 600;
            text-decoration: none;
            border-radius: 8px;
            transition: all 0.3s ease;
            cursor: pointer;
            border: none;
            font-family: inherit;
          }

          .btn-primary {
            background: #10EFC7;
            color: #000000;
          }

          .btn-primary:hover {
            background: #00614F;
            color: #ffffff;
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(16, 239, 199, 0.4);
          }

          .btn-secondary {
            background: transparent;
            color: #10EFC7;
            border: 1px solid #10EFC7;
          }

          .btn-secondary:hover {
            background: rgba(16, 239, 199, 0.1);
          }

          .footer-text {
            margin-top: 2rem;
            font-size: 0.875rem;
            color: rgba(255, 255, 255, 0.5);
          }

          .debug-section {
            margin-top: 1.5rem;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            padding-top: 1.5rem;
          }

          .debug-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 1rem;
          }

          .debug-title {
            font-size: 0.875rem;
            font-weight: 600;
            color: rgba(255, 255, 255, 0.6);
          }

          .error-id {
            font-size: 0.75rem;
            font-family: 'Courier New', monospace;
            color: #10EFC7;
            background: rgba(16, 239, 199, 0.1);
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
          }

          .btn-copy {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 1rem;
            font-size: 0.875rem;
            font-weight: 500;
            background: rgba(16, 239, 199, 0.1);
            color: #10EFC7;
            border: 1px solid rgba(16, 239, 199, 0.3);
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s ease;
            font-family: inherit;
          }

          .btn-copy:hover {
            background: rgba(16, 239, 199, 0.2);
            border-color: #10EFC7;
          }

          .btn-copy.copied {
            background: rgba(16, 239, 199, 0.3);
            color: #ffffff;
          }

          .btn-copy svg {
            width: 16px;
            height: 16px;
          }

          @media (max-width: 640px) {
            .error-card {
              padding: 1.5rem;
            }

            .error-title {
              font-size: 1.5rem;
            }

            .button-group {
              flex-direction: column;
            }

            .btn {
              width: 100%;
            }
          }
        `}</style>
      </head>
      <body>
        <div className="error-container">
          <div className="error-content">
            <div className="error-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            <div className="error-card">
              {statusCode && <div className="status-code">{statusCode}</div>}

              <h1 className="error-title">{errorTitle}</h1>

              <p className="error-message">{errorMessage}</p>

              {process.env.NODE_ENV === "development" &&
                error instanceof Error &&
                error.stack && (
                  <div className="error-details">
                    <div className="error-details-title">
                      Error Details (Development Only):
                    </div>
                    <pre className="error-stack">{error.stack}</pre>
                  </div>
                )}

              <div className="button-group">
                <a href="/" className="btn btn-primary">
                  Take Me Home
                </a>
                <button
                  onClick={() => window.history.back()}
                  className="btn btn-secondary"
                  type="button"
                >
                  Go Back
                </button>
              </div>

              {isHydrated && (
                <div className="debug-section">
                  <div className="debug-header">
                    <span className="debug-title">Troubleshooting Info</span>
                    <span className="error-id">{errorId}</span>
                  </div>
                  <button
                    onClick={handleCopyDebugInfo}
                    className={`btn-copy ${copied ? "copied" : ""}`}
                    type="button"
                  >
                    {copied ? (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        Copy Debug Info
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            <p className="footer-text">
              If this problem persists, please contact support.
            </p>
          </div>
        </div>
        <Scripts />
      </body>
    </html>
  );
}
